import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Plus, Search, Filter, Mail, FileText, Upload, Download,
    Trash2, X, ChevronRight, Building2, MapPin, Phone, ExternalLink,
    Flame, CheckCircle2, AlertCircle,
} from "lucide-react";

const STATUS_LABELS = {
    a_contacter: "À contacter",
    contacte: "Contacté",
    relance_1: "Relance 1",
    relance_2: "Relance 2",
    demo_prevue: "Démo prévue",
    gagne: "🏆 Gagné",
    perdu: "Perdu",
};

const STATUS_COLORS = {
    a_contacter: "bg-slate-100 text-slate-700",
    contacte: "bg-blue-100 text-blue-800",
    relance_1: "bg-amber-100 text-amber-800",
    relance_2: "bg-orange-100 text-orange-800",
    demo_prevue: "bg-violet-100 text-violet-800",
    gagne: "bg-emerald-100 text-emerald-800",
    perdu: "bg-rose-100 text-rose-800",
};

const SECTOR_LABELS = {
    restauration: "Restauration",
    hotellerie_evenementiel: "Hôtellerie & Événementiel",
    nettoyage: "Nettoyage",
    logistique: "Logistique",
};

const TEMPLATE_LABELS = {
    first_touch: "1er contact",
    relance_1: "Relance 1",
    relance_2: "Relance 2 (dernière chance)",
    demo_invite: "Confirmation démo",
    post_demo: "Suite post-démo",
};

const PRIORITY_LABELS = { 1: "🔥 Chaud", 2: "☕ Tiède", 3: "❄️ Froid" };
const AURA_CITIES = ["Lyon", "Villeurbanne", "Grenoble", "Meylan", "Valence", "Bourg-lès-Valence"];

export default function AdminProspection() {
    const [prospects, setProspects] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("kanban"); // kanban | list
    const [filters, setFilters] = useState({ status: "", city: "", sector: "", search: "" });
    const [selected, setSelected] = useState(null);
    const [showNew, setShowNew] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
            const [pRes, sRes] = await Promise.all([
                api.get(`/admin/prospects?${params.toString()}`),
                api.get("/admin/prospects/stats"),
            ]);
            setProspects(pRes.data);
            setStats(sRes.data);
        } catch (e) {
            toast.error("Erreur chargement prospects : " + (e.response?.data?.detail || e.message));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filters.status, filters.city, filters.sector]);

    const filtered = useMemo(() => {
        if (!filters.search) return prospects;
        const s = filters.search.toLowerCase();
        return prospects.filter((p) =>
            (p.company_name || "").toLowerCase().includes(s) ||
            (p.contact_name || "").toLowerCase().includes(s) ||
            (p.contact_email || "").toLowerCase().includes(s)
        );
    }, [prospects, filters.search]);

    const byStatus = useMemo(() => {
        const g = {};
        Object.keys(STATUS_LABELS).forEach((k) => (g[k] = []));
        filtered.forEach((p) => { if (g[p.status]) g[p.status].push(p); });
        return g;
    }, [filtered]);

    const exportCsv = async () => {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => { if (v && k !== "search") params.append(k, v); });
        try {
            const res = await api.get(`/admin/prospects/export.csv?${params.toString()}`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = "prospects_renforteo.csv";
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success("Export CSV téléchargé");
        } catch (e) {
            toast.error("Export échoué : " + (e.response?.data?.detail || e.message));
        }
    };

    const importCsv = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/admin/prospects/import-csv", fd);
            toast.success(`${data.created} prospects importés · ${data.skipped} ignorés`);
            if (data.errors?.length) console.warn("Import errors:", data.errors);
            refresh();
        } catch (e) {
            toast.error("Import échoué : " + (e.response?.data?.detail || e.message));
        }
    };

    return (
        <section className="space-y-6" data-testid="admin-prospection-tab">
            {/* KPIs */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="prospection-kpis">
                    <div className="sd-card p-4">
                        <div className="text-xs text-slate-500 mb-1">Prospects actifs</div>
                        <div className="text-2xl font-display text-[#0B192C]">{stats.total_active}</div>
                    </div>
                    <div className="sd-card p-4">
                        <div className="text-xs text-slate-500 mb-1">Relances en retard</div>
                        <div className="text-2xl font-display text-amber-600 flex items-center gap-2">
                            {stats.overdue_followups}
                            {stats.overdue_followups > 0 && <AlertCircle size={16} />}
                        </div>
                    </div>
                    <div className="sd-card p-4">
                        <div className="text-xs text-slate-500 mb-1">Démos prévues</div>
                        <div className="text-2xl font-display text-violet-700">{stats.counts_by_status?.demo_prevue || 0}</div>
                    </div>
                    <div className="sd-card p-4">
                        <div className="text-xs text-slate-500 mb-1">Taux de conversion</div>
                        <div className="text-2xl font-display text-emerald-700">{stats.conversion_rate_pct} %</div>
                    </div>
                </div>
            )}

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2">
                <button
                    onClick={() => setShowNew(true)}
                    className="btn-gold text-sm"
                    data-testid="prospection-new-btn"
                >
                    <Plus size={14} className="inline -mt-0.5 mr-1" />
                    Nouveau prospect
                </button>
                <label className="btn-ghost-navy text-sm cursor-pointer" data-testid="prospection-import-btn">
                    <Upload size={14} className="inline -mt-0.5 mr-1" />
                    Importer CSV
                    <input
                        type="file"
                        accept=".csv"
                        className="hidden"
                        onChange={(e) => {
                            if (e.target.files?.[0]) importCsv(e.target.files[0]);
                        }}
                    />
                </label>
                <button
                    onClick={exportCsv}
                    className="btn-ghost-navy text-sm"
                    data-testid="prospection-export-btn"
                >
                    <Download size={14} className="inline -mt-0.5 mr-1" />
                    Exporter CSV
                </button>
                <div className="flex-1" />
                <div className="flex items-center gap-1 border border-slate-200 rounded-lg p-1">
                    <button
                        onClick={() => setView("kanban")}
                        className={`px-3 py-1 text-xs rounded ${view === "kanban" ? "bg-[#0B192C] text-white" : "text-slate-600"}`}
                        data-testid="view-kanban-btn"
                    >
                        Kanban
                    </button>
                    <button
                        onClick={() => setView("list")}
                        className={`px-3 py-1 text-xs rounded ${view === "list" ? "bg-[#0B192C] text-white" : "text-slate-600"}`}
                        data-testid="view-list-btn"
                    >
                        Liste
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2 items-center">
                <div className="relative">
                    <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
                    <input
                        value={filters.search}
                        onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                        placeholder="Recherche…"
                        className="pl-9 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg w-52"
                        data-testid="prospection-search"
                    />
                </div>
                <select
                    value={filters.city}
                    onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}
                    className="text-sm border border-slate-200 rounded-lg px-2 py-1.5"
                    data-testid="prospection-filter-city"
                >
                    <option value="">Toutes villes</option>
                    {AURA_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select
                    value={filters.sector}
                    onChange={(e) => setFilters((f) => ({ ...f, sector: e.target.value }))}
                    className="text-sm border border-slate-200 rounded-lg px-2 py-1.5"
                    data-testid="prospection-filter-sector"
                >
                    <option value="">Tous secteurs</option>
                    {Object.entries(SECTOR_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                    ))}
                </select>
                <select
                    value={filters.status}
                    onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                    className="text-sm border border-slate-200 rounded-lg px-2 py-1.5"
                    data-testid="prospection-filter-status"
                >
                    <option value="">Tous statuts</option>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                    ))}
                </select>
                <button
                    onClick={refresh}
                    className="btn-ghost-navy text-xs"
                    data-testid="prospection-refresh"
                >
                    Rafraîchir
                </button>
            </div>

            {loading && <div className="text-slate-400 text-sm py-8 text-center">Chargement…</div>}

            {!loading && view === "kanban" && (
                <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="prospection-kanban">
                    {Object.entries(STATUS_LABELS).map(([status, label]) => (
                        <div key={status} className="bg-slate-50 rounded-xl p-3 min-h-[240px]" data-testid={`kanban-col-${status}`}>
                            <div className="flex items-center justify-between mb-3">
                                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_COLORS[status]}`}>
                                    {label}
                                </span>
                                <span className="text-xs text-slate-500">{byStatus[status].length}</span>
                            </div>
                            <div className="space-y-2">
                                {byStatus[status].map((p) => (
                                    <button
                                        key={p.id}
                                        onClick={() => setSelected(p)}
                                        className="w-full text-left bg-white border border-slate-200 rounded-lg p-3 hover:border-[#F5A623] transition-colors"
                                        data-testid={`kanban-card-${p.id}`}
                                    >
                                        <div className="font-semibold text-sm text-[#0B192C] mb-1 line-clamp-1">
                                            {p.company_name}
                                        </div>
                                        <div className="text-xs text-slate-500 line-clamp-1">{p.contact_name || p.contact_email}</div>
                                        <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                                            <MapPin size={10} />
                                            <span>{p.city}</span>
                                            <span className="mx-1">·</span>
                                            <span>{PRIORITY_LABELS[p.priority]}</span>
                                        </div>
                                    </button>
                                ))}
                                {byStatus[status].length === 0 && (
                                    <div className="text-xs text-slate-400 text-center py-6">Vide</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!loading && view === "list" && (
                <div className="sd-card overflow-x-auto" data-testid="prospection-list">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                                <th className="py-3 px-3">Entreprise</th>
                                <th>Contact</th>
                                <th>Ville</th>
                                <th>Secteur</th>
                                <th>Priorité</th>
                                <th>Statut</th>
                                <th>Prochaine action</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((p) => (
                                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`list-row-${p.id}`}>
                                    <td className="py-3 px-3 font-semibold text-[#0B192C]">{p.company_name}</td>
                                    <td>
                                        <div className="text-slate-700">{p.contact_name || "—"}</div>
                                        <div className="text-xs text-slate-400">{p.contact_email}</div>
                                    </td>
                                    <td>{p.city}</td>
                                    <td className="text-xs">{SECTOR_LABELS[p.sector]}</td>
                                    <td className="text-xs">{PRIORITY_LABELS[p.priority]}</td>
                                    <td>
                                        <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLORS[p.status]}`}>
                                            {STATUS_LABELS[p.status]}
                                        </span>
                                    </td>
                                    <td className="text-xs text-slate-500">
                                        {p.next_action_at ? new Date(p.next_action_at).toLocaleDateString("fr-FR") : "—"}
                                    </td>
                                    <td>
                                        <button
                                            onClick={() => setSelected(p)}
                                            className="btn-ghost-navy text-xs"
                                            data-testid={`open-${p.id}`}
                                        >
                                            <ChevronRight size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="py-8 text-center text-slate-400">Aucun prospect</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {selected && (
                <ProspectDetailModal
                    prospect={selected}
                    onClose={() => setSelected(null)}
                    onChanged={refresh}
                />
            )}
            {showNew && (
                <NewProspectModal
                    onClose={() => setShowNew(false)}
                    onCreated={() => { setShowNew(false); refresh(); }}
                />
            )}
        </section>
    );
}


function NewProspectModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        company_name: "",
        siren: "",
        city: "Lyon",
        sector: "restauration",
        size: "PME",
        contact_name: "",
        contact_role: "",
        contact_email: "",
        contact_phone: "",
        linkedin_url: "",
        priority: 2,
        source: "cold_outreach",
    });
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        setSaving(true);
        try {
            await api.post("/admin/prospects", form);
            toast.success("Prospect créé");
            onCreated();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prospect-new-modal">
                <div className="flex justify-between mb-6">
                    <h3 className="font-display text-xl text-[#0B192C]">Nouveau prospect</h3>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Raison sociale *" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} testId="new-company-name" />
                    <Field label="SIREN" value={form.siren} onChange={(v) => setForm({ ...form, siren: v })} testId="new-siren" />
                    <Field label="Contact (nom prénom)" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testId="new-contact-name" />
                    <Field label="Rôle" value={form.contact_role} onChange={(v) => setForm({ ...form, contact_role: v })} placeholder="Ex: Directeur salle" />
                    <Field label="Email *" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} type="email" testId="new-contact-email" />
                    <Field label="Téléphone" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} />
                    <Field label="LinkedIn" value={form.linkedin_url} onChange={(v) => setForm({ ...form, linkedin_url: v })} placeholder="https://linkedin.com/..." />
                    <div>
                        <label className="text-xs text-slate-500 mb-1 block">Ville</label>
                        <select value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                            {AURA_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 mb-1 block">Secteur</label>
                        <select value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="new-sector">
                            {Object.entries(SECTOR_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 mb-1 block">Taille</label>
                        <select value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                            <option>TPE</option><option>PME</option><option>ETI</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 mb-1 block">Priorité</label>
                        <select value={form.priority} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="new-priority">
                            <option value={1}>🔥 Chaud</option>
                            <option value={2}>☕ Tiède</option>
                            <option value={3}>❄️ Froid</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-slate-500 mb-1 block">Source</label>
                        <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm">
                            <option value="cold_outreach">Cold outreach</option>
                            <option value="linkedin">LinkedIn</option>
                            <option value="sirha">Salon Sirha</option>
                            <option value="reference">Référence client</option>
                            <option value="site_web">Site web</option>
                        </select>
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onClose} className="btn-ghost-navy text-sm">Annuler</button>
                    <button
                        onClick={submit}
                        disabled={saving || !form.company_name || !form.contact_email}
                        className="btn-gold text-sm disabled:opacity-50"
                        data-testid="new-prospect-save"
                    >
                        {saving ? "Création…" : "Créer"}
                    </button>
                </div>
            </div>
        </div>
    );
}


function Field({ label, value, onChange, type = "text", placeholder, testId }) {
    return (
        <div>
            <label className="text-xs text-slate-500 mb-1 block">{label}</label>
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                type={type}
                placeholder={placeholder}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                data-testid={testId}
            />
        </div>
    );
}


function ProspectDetailModal({ prospect, onClose, onChanged }) {
    const [p, setP] = useState(prospect);
    const [note, setNote] = useState("");
    const [tpl, setTpl] = useState("first_touch");
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/admin/prospects/${prospect.id}`);
            setP(data);
        } catch (_) {}
    };

    const changeStatus = async (newStatus) => {
        setBusy(true);
        try {
            const { data } = await api.patch(`/admin/prospects/${p.id}`, { status: newStatus });
            setP(data);
            toast.success("Statut mis à jour");
            onChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setBusy(false);
        }
    };

    const addNote = async () => {
        if (!note.trim()) return;
        setBusy(true);
        try {
            const { data } = await api.patch(`/admin/prospects/${p.id}`, { manual_note: note });
            setP(data);
            setNote("");
            toast.success("Note ajoutée");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        } finally {
            setBusy(false);
        }
    };

    const sendEmail = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/prospects/${p.id}/send-email`, { template_key: tpl });
            if (data?.ok) {
                toast.success(`Email « ${TEMPLATE_LABELS[tpl]} » envoyé à ${p.contact_email}`);
            } else {
                toast.warning(`Statut mis à jour, mais l'email n'a pas pu être envoyé (Resend a répondu 401). Vérifiez la clé Emergent Email.`);
            }
            await load();
            onChanged();
        } catch (e) {
            toast.error("Envoi échoué : " + (e.response?.data?.detail || e.message));
        } finally {
            setBusy(false);
        }
    };

    const openQuoteLink = async () => {
        try {
            const { data } = await api.get(`/admin/prospects/${p.id}/quote-link`);
            window.open(data.url, "_blank");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    };

    const archive = async () => {
        if (!window.confirm("Archiver ce prospect ?")) return;
        try {
            await api.delete(`/admin/prospects/${p.id}`);
            toast.success("Archivé");
            onChanged();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur");
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`prospect-detail-${p.id}`}>
                {/* Header */}
                <div className="p-6 border-b border-slate-200 sticky top-0 bg-white z-10">
                    <div className="flex justify-between items-start mb-2">
                        <div>
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Building2 size={14} />
                                <span>{p.company_name}</span>
                                {p.priority === 1 && <Flame size={12} className="text-rose-600" />}
                            </div>
                            <h3 className="font-display text-xl text-[#0B192C] mt-1">
                                {p.contact_name || p.contact_email}
                            </h3>
                            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mt-2">
                                <span className="inline-flex items-center gap-1"><MapPin size={11} />{p.city}</span>
                                <span>·</span>
                                <span>{SECTOR_LABELS[p.sector]}</span>
                                {p.contact_phone && (<><span>·</span><span className="inline-flex items-center gap-1"><Phone size={11} />{p.contact_phone}</span></>)}
                                {p.linkedin_url && (<><span>·</span><a href={p.linkedin_url} target="_blank" rel="noreferrer" className="text-[#F5A623] inline-flex items-center gap-1">LinkedIn <ExternalLink size={10} /></a></>)}
                            </div>
                        </div>
                        <button onClick={onClose}><X size={18} /></button>
                    </div>
                </div>

                {/* Actions */}
                <div className="p-6 space-y-4">
                    <div>
                        <div className="text-xs text-slate-500 mb-2">Statut du pipeline</div>
                        <div className="flex flex-wrap gap-1.5">
                            {Object.entries(STATUS_LABELS).map(([k, v]) => (
                                <button
                                    key={k}
                                    disabled={busy}
                                    onClick={() => changeStatus(k)}
                                    className={`text-xs px-2 py-1 rounded-full transition-all ${p.status === k ? STATUS_COLORS[k] + " ring-2 ring-[#F5A623]" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
                                    data-testid={`status-${k}`}
                                >
                                    {v}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-slate-50 rounded-xl p-4">
                            <div className="text-xs text-slate-500 mb-2 flex items-center gap-1"><Mail size={12} /> Envoyer un email Resend</div>
                            <select value={tpl} onChange={(e) => setTpl(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mb-2" data-testid="tpl-select">
                                {Object.entries(TEMPLATE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                            <button
                                disabled={busy}
                                onClick={sendEmail}
                                className="btn-gold text-xs w-full"
                                data-testid="send-email-btn"
                            >
                                Envoyer maintenant
                            </button>
                        </div>
                        <div className="bg-slate-50 rounded-xl p-4">
                            <div className="text-xs text-slate-500 mb-2 flex items-center gap-1"><FileText size={12} /> Conversion → devis</div>
                            <p className="text-xs text-slate-500 mb-2 leading-snug">
                                Ouvre le configurateur pré-rempli avec le secteur et la ville du prospect.
                            </p>
                            <button
                                onClick={openQuoteLink}
                                className="btn-navy text-xs w-full"
                                data-testid="quote-link-btn"
                            >
                                Générer devis pré-rempli
                            </button>
                        </div>
                    </div>

                    <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-xs text-slate-500 mb-2">Ajouter une note</div>
                        <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={2}
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm mb-2"
                            placeholder="Ex: Appelé le 15/02 — intéressé, à recontacter la semaine prochaine."
                            data-testid="note-input"
                        />
                        <button disabled={busy || !note.trim()} onClick={addNote} className="btn-ghost-navy text-xs" data-testid="add-note-btn">
                            Ajouter à la timeline
                        </button>
                    </div>

                    {/* Timeline */}
                    <div>
                        <div className="text-xs text-slate-500 mb-2">Historique</div>
                        <div className="space-y-2">
                            {(p.notes || []).slice().reverse().map((n) => (
                                <div key={n.id} className="flex gap-2 text-xs" data-testid={`note-${n.id}`}>
                                    <div className="w-1 rounded-full bg-slate-300" />
                                    <div className="flex-1">
                                        <div className="flex justify-between text-slate-500">
                                            <span>
                                                {n.kind === "email_sent" && <><Mail size={10} className="inline mr-1" />Email envoyé : {TEMPLATE_LABELS[n.template_key] || n.template_key}</>}
                                                {n.kind === "status_change" && <><CheckCircle2 size={10} className="inline mr-1" />{n.content}</>}
                                                {n.kind === "note" && <>Note : {n.content}</>}
                                                {["call", "meeting"].includes(n.kind) && <>{n.kind} : {n.content}</>}
                                            </span>
                                            <span className="text-slate-400">{new Date(n.created_at).toLocaleString("fr-FR")}</span>
                                        </div>
                                        {n.kind === "email_sent" && <div className="text-slate-400 mt-0.5">Par {n.author} · {n.content}</div>}
                                    </div>
                                </div>
                            ))}
                            {(p.notes || []).length === 0 && (
                                <div className="text-slate-400 text-xs">Aucun événement.</div>
                            )}
                        </div>
                    </div>

                    <div className="pt-4 border-t border-slate-200 flex justify-between items-center">
                        <button onClick={archive} className="text-xs text-rose-600 hover:underline inline-flex items-center gap-1" data-testid="archive-btn">
                            <Trash2 size={12} /> Archiver
                        </button>
                        <div className="text-xs text-slate-400">
                            Créé le {new Date(p.created_at).toLocaleDateString("fr-FR")}
                            {p.next_action_at && (
                                <> · Prochaine relance auto le <b>{new Date(p.next_action_at).toLocaleDateString("fr-FR")}</b></>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
